// Drop-in replacement
export class MediaRecorderService {
    mediaRecorder = null;
    stream = null;
    chunks = [];
    bytesSoFar = 0;
    startedAt = 0;
    sliceTimer = null;
    mimeType = 'audio/webm;codecs=opus';
    stopped = false;
    pendingRestart = false;
    // Web Audio metering
    audioCtx = null;
    analyser = null;
    sourceNode = null;
    levelRaf = null;
    // thresholds (tweak as you like)
    maxBytes = 5 * 1024 * 1024; // 5MB per segment
    maxDurationMs = 60_000; // 60s per segment
    // -------- Input issue detection config & state --------
    SILENCE_THRESHOLD = 0.03; // 0..1 level below which we consider silence
    SILENCE_MS = 7000; // sustain silence for this long => issue
    NO_DATA_GRACE_MS = 3000; // after start, no bytes for this long => issue
    lastAboveThreshold = 0; // ms timestamp of last time level > threshold
    issueActive = false; // latched until resolved
    // original callbacks
    onChunk = (chunkFile) => { };
    onAudio = (audioFile) => { };
    onFinalAudio = (audioFile) => { };
    // live audio level 0..1
    onLevel = (level) => { };
    // new input-issue callbacks
    onInputIssue = (issue) => { };
    onInputIssueResolved = () => { };
    setOnChunk = (fn) => { this.onChunk = fn; };
    setOnAudio = (fn) => { this.onAudio = fn; };
    setOnFinalAudio = (fn) => { this.onFinalAudio = fn; };
    setOnLevel = (fn) => { this.onLevel = fn; };
    setOnInputIssue = (fn) => { this.onInputIssue = fn; };
    setOnInputIssueResolved = (fn) => { this.onInputIssueResolved = fn; };
    /**
     * Backward-compatible:
     * - timesliceMs controls how often we emit a cumulative "so-far" file via onChunk.
     * - To customize size/duration cutoffs, call setLimits() before start().
     */
    async start(timesliceMs = 250) {
        if (this.mediaRecorder)
            return;
        this.stopped = false;
        try {
            if (!this.stream) {
                // @TODO: FLU-193 Handle this.
                this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            // Initialize detection state
            const now = Date.now();
            this.lastAboveThreshold = now;
            this.issueActive = false;
            // Attach track-level diagnostics
            const track = this.stream.getAudioTracks()[0];
            if (track) {
                track.onmute = () => this.raiseIssueOnce('track-muted', 'Your microphone is muted or not sending audio.');
                track.onunmute = () => this.resolveIssue();
                track.onended = () => this.raiseIssueOnce('track-ended', 'The selected microphone was disconnected.');
            }
            // Setup Web Audio metering
            this.setupMetering();
            // Pick a supported mime
            if (typeof MediaRecorder !== 'undefined') {
                if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                    this.mimeType = 'audio/webm;codecs=opus';
                }
                else if (MediaRecorder.isTypeSupported('audio/webm')) {
                    this.mimeType = 'audio/webm';
                }
                else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                    this.mimeType = 'audio/ogg;codes=opus'; // typo? keep webm preferred
                    this.mimeType = 'audio/ogg;codecs=opus';
                }
                else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                    this.mimeType = 'audio/mp4';
                }
                else {
                    this.mimeType = ''; // let browser decide
                }
            }
            this.startSegment(timesliceMs);
            // No-data watchdog: after a short grace, if no bytes, surface issue
            window.setTimeout(() => {
                if (!this.stopped && this.bytesSoFar === 0) {
                    this.raiseIssueOnce('no-data', 'No audio is being captured. Check your input device and permissions.');
                }
            }, this.NO_DATA_GRACE_MS);
        }
        catch (e) {
            // Translate common gUM failures into an issue the UI can show
            const code = e?.name || 'gum-error';
            const message = e?.message || 'Unable to access microphone.';
            try {
                this.onInputIssue({ code, message });
            }
            catch { }
            this.cleanup(true);
            throw e;
        }
    }
    stop() {
        this.stopped = true;
        if (this.sliceTimer !== null) {
            clearInterval(this.sliceTimer);
            this.sliceTimer = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop(); // onstop emits final segment + cleanup
        }
        else {
            this.cleanup(true);
        }
    }
    /**
     * Optional: configure segment limits before start()
     */
    setLimits({ maxBytes, maxDurationMs }) {
        if (typeof maxBytes === 'number')
            this.maxBytes = maxBytes;
        if (typeof maxDurationMs === 'number')
            this.maxDurationMs = maxDurationMs;
    }
    // ---------------- internals ----------------
    setupMetering() {
        try {
            if (!this.stream)
                return;
            // Reuse existing context if available
            if (!this.audioCtx) {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (!AC)
                    return; // not supported
                this.audioCtx = new AC();
            }
            // Ensure context is running
            try {
                if (this.audioCtx.state === 'suspended')
                    this.audioCtx.resume();
            }
            catch { }
            if (!this.analyser) {
                this.analyser = this.audioCtx.createAnalyser();
                this.analyser.fftSize = 2048;
                this.analyser.smoothingTimeConstant = 0.8;
            }
            if (!this.sourceNode) {
                this.sourceNode = this.audioCtx.createMediaStreamSource(this.stream);
            }
            // Connect graph: source -> analyser (no destination needed)
            try {
                this.sourceNode.connect(this.analyser);
            }
            catch { }
            const bufferLength = this.analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            const loop = () => {
                if (!this.analyser)
                    return;
                this.analyser.getByteTimeDomainData(dataArray);
                // Compute RMS around 128 midpoint
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const v = (dataArray[i] - 128) / 128; // -1..1
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / dataArray.length); // 0..1
                // Map to a friendlier range, apply mild gain and clamp
                const level = Math.max(0, Math.min(1, rms * 1.8));
                try {
                    this.onLevel(level);
                }
                catch { }
                // Silence detector using level
                const now = Date.now();
                if (level > this.SILENCE_THRESHOLD) {
                    this.lastAboveThreshold = now;
                    if (this.issueActive) {
                        this.resolveIssue();
                    }
                }
                else {
                    const silentFor = now - this.lastAboveThreshold;
                    if (!this.issueActive && silentFor >= this.SILENCE_MS) {
                        this.raiseIssueOnce('silence', 'We are not detecting microphone input. Is the right input device selected?');
                    }
                }
                this.levelRaf = requestAnimationFrame(loop);
            };
            this.levelRaf = requestAnimationFrame(loop);
        }
        catch (e) {
            // Ignore metering errors; recording will still work
            console.warn('Audio metering unavailable:', e);
        }
    }
    teardownMetering() {
        if (this.levelRaf !== null) {
            cancelAnimationFrame(this.levelRaf);
            this.levelRaf = null;
        }
        try {
            if (this.sourceNode) {
                try {
                    this.sourceNode.disconnect();
                }
                catch { }
                this.sourceNode = null;
            }
            if (this.analyser) {
                try {
                    this.analyser.disconnect();
                }
                catch { }
                this.analyser = null;
            }
            if (this.audioCtx) {
                // Closing AudioContext can be slow; suspend instead to allow reuse
                try {
                    this.audioCtx.suspend();
                }
                catch { }
                // Do not set to null so we can resume quickly on next start
            }
        }
        catch { }
        // Notify zero level after tear-down
        try {
            this.onLevel(0);
        }
        catch { }
    }
    startSegment(timesliceMs) {
        if (!this.stream)
            return;
        // reset per-segment
        this.chunks = [];
        this.bytesSoFar = 0;
        this.startedAt = Date.now();
        this.pendingRestart = false;
        this.mediaRecorder = new MediaRecorder(this.stream, this.mimeType ? { mimeType: this.mimeType } : {});
        this.mediaRecorder.ondataavailable = (e) => {
            if (!e.data || !e.data.size)
                return;
            this.chunks.push(e.data);
            this.bytesSoFar += e.data.size;
            // Size / duration guard: if exceeded, stop -> onstop emits and restarts
            const dur = Date.now() - this.startedAt;
            if (!this.stopped && (this.bytesSoFar >= this.maxBytes || dur >= this.maxDurationMs)) {
                this.pendingRestart = true;
                // Stop timeslice polling before stopping recorder to avoid races
                if (this.sliceTimer !== null) {
                    clearInterval(this.sliceTimer);
                    this.sliceTimer = null;
                }
                this.mediaRecorder?.stop();
            }
        };
        this.mediaRecorder.onstop = () => {
            const file = this.buildFileFromChunks();
            if (file) {
                // Per closed segment
                this.onAudio(file);
                if (this.stopped) {
                    // Last segment when user stopped session
                    this.onFinalAudio(file);
                }
            }
            this.mediaRecorder = null;
            if (!this.stopped && this.pendingRestart) {
                // Start a brand-new segment immediately
                this.startSegment(timesliceMs);
            }
            else if (this.stopped) {
                this.cleanup(true);
            }
        };
        // Start continuous capture; we’ll pull data with requestData for previews
        this.mediaRecorder.start();
        // Emit cumulative "so far" files at a cadence for UI / progressive upload
        if (timesliceMs > 0) {
            this.sliceTimer = window.setInterval(() => {
                if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
                    // Triggers ondataavailable with a fragment; then we rebuild cumulative below
                    this.mediaRecorder.requestData();
                    // Build cumulative file (1..N) so every onChunk is a valid, standalone file
                    const cumulative = this.buildFileFromChunks();
                    if (cumulative)
                        this.onChunk(cumulative);
                }
            }, timesliceMs);
        }
    }
    buildFileFromChunks() {
        if (!this.chunks.length)
            return null;
        const type = this.mimeType || 'audio/webm';
        const blob = new Blob(this.chunks, { type });
        const ext = this.extFromMime();
        const file = new File([blob], `rec-${Date.now()}.${ext}`, { type });
        return file;
        // NOTE: This is the key: we ALWAYS build from all chunks so far.
        // That makes every onChunk upload valid (headers + complete timeline).
    }
    extFromMime() {
        const mt = (this.mimeType || '').toLowerCase();
        if (mt.includes('ogg'))
            return 'ogg';
        if (mt.includes('mp4'))
            return 'mp4';
        return 'webm';
    }
    cleanup(closeTracks = false) {
        if (this.sliceTimer !== null) {
            clearInterval(this.sliceTimer);
            this.sliceTimer = null;
        }
        // Stop metering
        this.teardownMetering();
        if (closeTracks && this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
        this.mediaRecorder = null;
        this.chunks = [];
        this.bytesSoFar = 0;
    }
    // -------- Input-issue helpers --------
    raiseIssueOnce(code, message) {
        if (this.issueActive)
            return;
        this.issueActive = true;
        try {
            this.onInputIssue({ code, message });
        }
        catch { }
    }
    resolveIssue() {
        if (!this.issueActive)
            return;
        this.issueActive = false;
        try {
            this.onInputIssueResolved();
        }
        catch { }
    }
}
